import type { CitizenClip, CitizenFigure } from './citizenAsset';
import { clipPoseAt, walkPose } from './citizenPose';

/**
 * What a citizen does with its body where it stands, and the pose it is in at a moment. A figure
 * speaks to someone or to no one: it talks only in company, sells to a buyer
 * from its own pitch, and takes up its tool only where its work is. Every number here is an
 * estimate of what reads well.
 */
export const ROLES = ['idle', 'talk', 'sell', 'buy', 'work', 'pray'] as const;
export type Role = (typeof ROLES)[number];

export const ACTING = {
  /** A figure settles from its walk into standing, from one thing into the next, and steps off
   *  again, over this long. */
  settleS: 0.45,
  /** Those standing take up something else, or keep at it, every so long. */
  turnS: 5,
  /** One who comes to stand with others greets them first, for the length of that clip. */
  greetS: 2,
  /** The share of its turns a seller speaks with a buyer before it. */
  sells: 0.65,
  /** The share of its turns a buyer speaks. */
  haggles: 0.45,
  /** The share of its turns one at work rests. */
  rests: 0.05,
  /** In talk a turn is taken as often as there are people to share it, and this much more. */
  eager: 0.1,
} as const;

/* Work with a tool or on a rope: done where the work is, and nowhere else. */
const TOOLS = new Set(['Work', 'Saw', 'Haul', 'Sweep']);

/* A basket on the arm or a sack on the shoulder leaves no hand to talk with. */
const handsFree = (figure: CitizenFigure): boolean =>
  figure.clips.idle !== 'IdleCarry' && figure.clips.idle !== 'IdleLoad';

/**
 * The clip a figure stands in for one turn. `keepers` and `visitors` are who stands in its
 * company, itself among them: those who keep the place, a seller or a hand, and those who came
 * to it. `lot`, 0 to 1, is the figure's own draw for the turn.
 */
export function standingClip(
  figure: CitizenFigure,
  role: Role,
  keepers: number,
  visitors: number,
  lot: number
): string {
  const { idle, work } = figure.clips;
  const speech = handsFree(figure) ? 'Talk' : idle;
  switch (role) {
    case 'pray':
      return 'Pray';
    case 'work':
      return TOOLS.has(work) && lot >= ACTING.rests ? work : idle;
    case 'sell':
      if (visitors > 0) return lot < ACTING.sells ? (work === 'Sell' ? work : speech) : idle;
      return idle;
    case 'buy':
      return keepers > 0 && lot < ACTING.haggles ? speech : idle;
    case 'talk': {
      const company = keepers + visitors;
      return company > 1 && lot < 1 / Math.min(3, company) + ACTING.eager ? speech : idle;
    }
    default:
      return idle;
  }
}

/** Whether a figure greets those it comes to stand with: one who came to talk or to buy, finds
 *  someone there, and has a hand to lift to its cap. */
export function greets(
  figure: CitizenFigure,
  role: Role,
  keepers: number,
  visitors: number
): boolean {
  if (!handsFree(figure)) return false;
  return role === 'buy' ? keepers > 0 : role === 'talk' && keepers + visitors > 1;
}

/** A figure at a moment: walking, or standing in one clip after another. */
export interface Act {
  moving: boolean;
  /** Metres walked, and the figure's size against life size. */
  walked: number;
  scale: number;
  walk: CitizenClip;
  /** Seconds since it came to a stand, or walked on from one. */
  turned: number;
  /** The clips it stands in this turn and stood in the last, how long this turn has run, and the
   *  time its clips are played at. */
  now: CitizenClip;
  before: CitizenClip;
  into: number;
  clock: number;
  /** The greeting it makes as it comes to a stand, if it makes one. */
  greet: CitizenClip | null;
}

/** A pose for the batch (citizenBatch.ts): `mix` of the way from `from` to `to`. */
export interface ActedPose {
  from: number;
  to: number;
  mix: number;
}

const eased = (t: number): number => t * t * (3 - 2 * t);

function posed(out: ActedPose, from: number, to: number, share: number): ActedPose {
  out.from = from;
  out.to = to;
  out.mix = from === to ? 0 : eased(Math.max(0, Math.min(1, share)));
  return out;
}

/** The pose of a figure walking or standing, settling from each thing it does into the next. It is
 *  written into `out`, so a crowd is posed without making anything. */
export function actedPose(act: Act, out: ActedPose = { from: 0, to: 0, mix: 0 }): ActedPose {
  const settle = ACTING.settleS;
  const walk = walkPose(act.walk, act.walked, act.scale);
  const stand = clipPoseAt(act.now, act.clock);
  if (act.moving)
    return act.turned < settle
      ? posed(out, stand, walk, act.turned / settle)
      : posed(out, walk, walk, 0);
  if (act.greet && act.turned < ACTING.greetS) {
    const greeting = clipPoseAt(act.greet, act.turned);
    if (act.turned < settle) return posed(out, walk, greeting, act.turned / settle);
    return posed(out, greeting, stand, (act.turned - (ACTING.greetS - settle)) / settle);
  }
  if (act.turned < settle) return posed(out, walk, stand, act.turned / settle);
  if (act.into < settle && act.before !== act.now)
    return posed(out, clipPoseAt(act.before, act.clock), stand, act.into / settle);
  return posed(out, stand, stand, 0);
}
